import React, { useContext, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCanvas } from '../../../context/CanvasContext';
import { useEditorUI } from '../../../context/EditorUIContext';
import EditSidebarTabs from './EditSidebarTabs';
import ChoicesList from './ChoicesList';
import StyledSwitch from './StyledSwitch';
import UploadModal from './UploadModal';
import PDFPreviewModal from './PDFPreviewModal';
import textStyles from './styles/EditSidebarText.module.css';
import settingsStyles from './styles/EditSidebarSettings.module.css';
import keyStyles from './styles/EditSidebarKeys.module.css';
import DeleteModal from './DeleteModal';
import useIsMobile from '../../../utils/useIsMobile';
import MediaPlayer from '../../../utils/MediaPlayer';
import { getMediaSrc } from '../../../utils/utils';
import TextEditor from '../../texteditor/TextEditor';
import tabStyles from './styles/EditSidebarTabs.module.css';
import { FaPlay, FaExpand, FaFileWord } from 'react-icons/fa';
import ScenarioTextEditorModal from './ScenarioTextEditorModal';
import { Tooltip } from 'react-tooltip';
import AuthContext from '../../../context/AuthContext';
import { TUTORIAL_TARGETS, tutorialAttr, useTutorialLock } from '../../../tutorials';
import KeyListEditor from './keys/KeyListEditor';
import { KEY_LIST_FIELDS } from './keys/keyGraph';

/**
 * Edits one situation.
 *
 * The subject is looked up from the graph by id on every render, so it is always
 * the current draft — there is no `selectedSituation` copy to fall out of date.
 * Each field commits on blur as an `updateNodeData` op, which applies locally at
 * once and is batched to the server behind a short debounce.
 *
 * @param {{nodeId: string}} props
 */
const EditSituation = ({ nodeId, setRenderEditSidebar }) => {
    const {
        scenario,
        getNode,
        getOutgoingEdges,
        updateSituation,
        deleteSituation,
        clearMedia,
        flushOps,
    } = useCanvas();

    const {
        activeEditTab,
        selectedEdgeIdsUser,
        selectedNodeQueue,
        expandedEditorNodeId,
        setExpandedEditorNodeId,
    } = useEditorUI();
    const { userId: authUserId } = useContext(AuthContext);

    const navigate = useNavigate();
    const isMobile = useIsMobile();
    // A tutorial writes this situation's content itself, so the fields show what
    // it wrote and refuse edits rather than inviting them.
    const locked = useTutorialLock('editor');

    const node = getNode(nodeId);
    const data = node?.data || {};
    const choices = getOutgoingEdges(nodeId);

    // Text inputs are the one place local state is still needed: they have to
    // stay responsive between keystrokes and only commit on blur. EditSidebar
    // keys this panel by node id, so they are seeded once per situation and an
    // op landing for this node never clobbers what the author is typing.
    const [title, setTitle] = useState(data.title || '');
    const [text, setText] = useState(data.text || '');
    const [audioLabel, setAudioLabel] = useState(data.audioLabel || '');

    const [activeSubTab, setActiveSubTab] = useState('text');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [uploadType, setUploadType] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const isTextEditorModalOpen = expandedEditorNodeId === nodeId;
    const [isPDFPreviewOpen, setIsPDFPreviewOpen] = useState(false);
    const [isConverting, setIsConverting] = useState(false);

    const editorRef = useRef(null);
    const docxInputRef = useRef(null);

    const choiceIds = choices.map((edge) => edge.id);

    // Somebody else has one of this situation's choices open; deleting the
    // situation would pull it out from under them.
    const deleteAvailable = !Object.values(selectedEdgeIdsUser).some((edgeId) =>
        choiceIds.includes(edgeId)
    );

    // First in the queue holds the edit; everyone else waits.
    const queue = selectedNodeQueue[nodeId];
    const isWaiting = !!queue && queue[0] !== authUserId;

    const commit = (patch) => updateSituation(nodeId, patch);

    /**
     * Previews from this situation.
     *
     * The preview loads the draft from the server, so whatever the author is
     * still typing has to be committed and sent first — clicking the button
     * does not reliably blur the field in time. Only fields that actually
     * differ are committed, and an empty queue flushes without a request, so
     * previewing an unchanged situation costs nothing.
     *
     * `returnTo` rides along so exiting the preview lands back on this
     * situation, in the expanded editor if that is where it was started.
     *
     * @param {{latestText?: string, expanded?: boolean}} [options]
     */
    const startSituationPreview = async ({ latestText = text, expanded = false } = {}) => {
        const patch = {};
        if (title !== (data.title || '')) patch.title = title;
        if (latestText !== (data.text || '')) patch.text = latestText;
        if (Object.keys(patch).length > 0) {
            setText(latestText);
            commit(patch);
        }
        await flushOps();
        navigate(`/create/${scenario._id}/preview`, {
            state: { currentSituationId: nodeId, returnTo: { nodeId, expanded } },
        });
    };

    /**
     * Imports a Word document at the cursor through the editor's importWord
     * command. The editor shows progress and any problems itself, and commits
     * the result through onBlur once it lands.
     */
    const handleDocxImport = async (event) => {
        const file = event.target.files[0];
        event.target.value = '';
        const editor = editorRef.current;
        if (!file || !editor || editor.state === 'destroyed') return;
        setIsConverting(true);
        try {
            await editor.execute('importWord', { file });
        } catch (err) {
            // Already reported in the editor's import banner.
        } finally {
            setIsConverting(false);
        }
    };

    const openModal = (type) => {
        setUploadType(type);
        setIsModalOpen(true);
    };

    /**
     * Endpoint type is a single field rather than two booleans, so a situation
     * cannot be both the start and an end. Toggling the switch off returns it to
     * an ordinary situation.
     */
    const handleEndpointChange = (which, value) => {
        commit({ endpointType: value ? which : '' });
    };

    const confirmDeleteSituation = () => {
        deleteSituation(nodeId);
        setIsDeleteModalOpen(false);
        if (isMobile && typeof setRenderEditSidebar === 'function') setRenderEditSidebar(false);
    };

    if (!node) return null;
    if (isWaiting) return <div>Waiting</div>;

    return (
        <div className={tabStyles.typeSituation}>
            <EditSidebarTabs editType="situation" />

            {activeEditTab === '1' && (
                <>
                    <div className={textStyles.subTabsContainer}>
                        <div
                            className={`${textStyles.subTab} ${
                                activeSubTab === 'text' ? textStyles.activeSubTab : ''
                            }`}
                            onClick={() => setActiveSubTab('text')}
                        >
                            Text
                        </div>
                        <div
                            className={`${textStyles.subTab} ${
                                activeSubTab === 'media' ? textStyles.activeSubTab : ''
                            }`}
                            onClick={() => setActiveSubTab('media')}
                        >
                            Media
                        </div>
                    </div>

                    <div className={textStyles.textContainer}>
                        {activeSubTab === 'text' && (
                            <>
                                <div className={textStyles.titleRow}>
                                    <button
                                        className={textStyles.previewPlayButton}
                                        onClick={() => startSituationPreview()}
                                        data-tooltip-id="preview_tooltip"
                                        data-tooltip-content="Preview Situation"
                                        {...tutorialAttr(TUTORIAL_TARGETS.EDIT_SITUATION_PREVIEW)}
                                    >
                                        <FaPlay />
                                    </button>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(event) => setTitle(event.target.value)}
                                        onBlur={() => {
                                            if (title !== (data.title || '')) commit({ title });
                                        }}
                                        className={textStyles.titleInput}
                                        readOnly={locked}
                                        {...tutorialAttr(TUTORIAL_TARGETS.EDIT_SITUATION_TITLE)}
                                    />
                                    <input
                                        type="file"
                                        accept=".docx,.docm,.dotx,.dotm"
                                        style={{ display: 'none' }}
                                        ref={docxInputRef}
                                        onChange={handleDocxImport}
                                    />
                                    <button
                                        className={textStyles.expandButton}
                                        onClick={() => docxInputRef.current.click()}
                                        data-tooltip-id="preview_tooltip"
                                        data-tooltip-content="Import Word Document"
                                        disabled={isConverting || locked}
                                    >
                                        <FaFileWord />
                                    </button>
                                    <button
                                        className={textStyles.expandButton}
                                        onClick={() => setExpandedEditorNodeId(nodeId)}
                                        data-tooltip-id="preview_tooltip"
                                        data-tooltip-content="Expand Editor"
                                        disabled={locked}
                                    >
                                        <FaExpand />
                                    </button>
                                </div>
                                <div className={textStyles.editorWrapper} {...tutorialAttr(TUTORIAL_TARGETS.EDIT_SITUATION_TEXT)}>
                                    <TextEditor
                                        initialData={text}
                                        onChange={setText}
                                        onBlur={(html) => {
                                            if (html !== (data.text || '')) commit({ text: html });
                                        }}
                                        editorRef={editorRef}
                                        readOnly={locked}
                                    />
                                </div>
                            </>
                        )}

                        {activeSubTab === 'media' && (
                            <>
                                {data.image && (
                                    <div className={textStyles.mediaPreview}>
                                        <img
                                            src={data.image}
                                            alt="Uploaded"
                                            className={textStyles.imagePreview}
                                        />
                                    </div>
                                )}
                                <div className={textStyles.buttonGroup}>
                                    <button
                                        onClick={() => openModal('image')}
                                        className={`${textStyles.buttonBase} ${textStyles.uploadButton}`}
                                    >
                                        {data.image ? 'Change Image' : 'Upload Image'}
                                    </button>
                                    {data.image && (
                                        <button
                                            onClick={() =>
                                                clearMedia({
                                                    target: 'node',
                                                    id: nodeId,
                                                    field: 'image',
                                                })
                                            }
                                            className={`${textStyles.buttonBase} ${textStyles.removeButton}`}
                                        >
                                            Remove Image
                                        </button>
                                    )}
                                </div>

                                {data.audio && (
                                    <>
                                        <input
                                            type="text"
                                            value={audioLabel}
                                            onChange={(event) => setAudioLabel(event.target.value)}
                                            onBlur={() => {
                                                if (audioLabel !== (data.audioLabel || '')) {
                                                    commit({ audioLabel });
                                                }
                                            }}
                                            className={textStyles.titleInput}
                                        />
                                        <div className={textStyles.mediaPreview}>
                                            <MediaPlayer
                                                src={getMediaSrc(data.audio)}
                                                autoplay={false}
                                                key={getMediaSrc(data.audio)}
                                            />
                                        </div>
                                    </>
                                )}

                                <div className={textStyles.buttonGroup}>
                                    <button
                                        onClick={() => openModal('audiovideo')}
                                        className={`${textStyles.buttonBase} ${textStyles.uploadButton}`}
                                    >
                                        {data.audio ? 'Change Audio/Video' : 'Upload Audio/Video'}
                                    </button>
                                    {data.audio && (
                                        <button
                                            onClick={() =>
                                                clearMedia({
                                                    target: 'node',
                                                    id: nodeId,
                                                    field: 'audio',
                                                })
                                            }
                                            className={`${textStyles.buttonBase} ${textStyles.removeButton}`}
                                        >
                                            Remove Audio/Video
                                        </button>
                                    )}
                                </div>

                                {data.document && (
                                    <div className={textStyles.mediaPreview}>
                                        <button
                                            onClick={() => setIsPDFPreviewOpen(true)}
                                            className={`${textStyles.buttonBase} ${textStyles.uploadButton}`}
                                        >
                                            View PDF
                                        </button>
                                    </div>
                                )}
                                <div className={textStyles.buttonGroup}>
                                    <button
                                        onClick={() => openModal('document')}
                                        className={`${textStyles.buttonBase} ${textStyles.uploadButton}`}
                                    >
                                        {data.document ? 'Change PDF' : 'Upload PDF'}
                                    </button>
                                    {data.document && (
                                        <button
                                            onClick={() =>
                                                clearMedia({
                                                    target: 'node',
                                                    id: nodeId,
                                                    field: 'document',
                                                })
                                            }
                                            className={`${textStyles.buttonBase} ${textStyles.removeButton}`}
                                        >
                                            Remove PDF
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}

            {activeEditTab === '2' && <ChoicesList nodeId={nodeId} />}

            {activeEditTab === '3' && (
                <div className={settingsStyles.settingsContainer}>
                    <div className={settingsStyles.settingItem}>
                        <label>Starting Situation</label>
                        <StyledSwitch
                            onChange={(value) => handleEndpointChange('start', value)}
                            checked={data.endpointType === 'start'}
                            className={settingsStyles.switch}
                        />
                    </div>
                    <div className={settingsStyles.settingItem}>
                        <label>Ending Situation</label>
                        <StyledSwitch
                            onChange={(value) => handleEndpointChange('end', value)}
                            checked={data.endpointType === 'end'}
                            disabled={choices.length > 0}
                            className={settingsStyles.switch}
                        />
                    </div>
                    <div className={settingsStyles.settingItem}>
                        <label>Open User Submission</label>
                        <StyledSwitch
                            onChange={(value) => commit({ isUserSubmitted: value })}
                            checked={!!data.isUserSubmitted}
                            className={settingsStyles.switch}
                        />
                    </div>
                    <div className={settingsStyles.settingItem}>
                        <label>Free-Response Matching</label>
                        <StyledSwitch
                            onChange={(value) => commit({ isFreeResponse: value })}
                            checked={!!data.isFreeResponse}
                            className={settingsStyles.switch}
                        />
                    </div>
                    <div className={settingsStyles.settingItem}>
                        <label>Track User Decisions</label>
                        <StyledSwitch
                            onChange={(value) => commit({ isLogged: value })}
                            checked={!!data.isLogged}
                            className={settingsStyles.switch}
                        />
                    </div>
                    <div className={settingsStyles.settingItem}>
                        <label>Autoplay Media</label>
                        <StyledSwitch
                            onChange={(value) => commit({ autoplay: value })}
                            checked={!!data.autoplay}
                            className={settingsStyles.switch}
                        />
                    </div>

                    {deleteAvailable && (
                        <div className={settingsStyles.deleteContainer}>
                            <button
                                onClick={() => setIsDeleteModalOpen(true)}
                                className={settingsStyles.deleteButton}
                                {...tutorialAttr(TUTORIAL_TARGETS.EDIT_SITUATION_DELETE)}
                            >
                                Delete Situation
                            </button>
                        </div>
                    )}
                </div>
            )}

            {activeEditTab === '4' && (
                <div className={keyStyles.keysContainer}>
                    <KeyListEditor
                        key={`grants-${nodeId}`}
                        field={KEY_LIST_FIELDS.GRANTS}
                        subject="situation"
                        data={data}
                        commit={commit}
                        tutorialTarget={TUTORIAL_TARGETS.EDIT_KEYS_ADD}
                    />
                    <KeyListEditor
                        key={`removes-${nodeId}`}
                        field={KEY_LIST_FIELDS.REMOVES}
                        subject="situation"
                        data={data}
                        commit={commit}
                        tutorialTarget={TUTORIAL_TARGETS.EDIT_KEYS_REMOVE}
                    />
                </div>
            )}

            {/* Modals */}
            {isTextEditorModalOpen && (
                <ScenarioTextEditorModal
                    onClose={() => setExpandedEditorNodeId(null)}
                    initialText={text}
                    onSave={(textToSave) => {
                        setText(textToSave);
                        commit({ text: textToSave });
                    }}
                    onPreview={(latestText) => startSituationPreview({ latestText, expanded: true })}
                    situationTitle={title}
                />
            )}

            {isModalOpen && (
                <UploadModal
                    uploadType={uploadType}
                    target={{ kind: 'node', id: nodeId }}
                    setIsModalOpen={setIsModalOpen}
                />
            )}

            {isPDFPreviewOpen && data.document && (
                <PDFPreviewModal
                    url={data.document}
                    onClose={() => setIsPDFPreviewOpen(false)}
                />
            )}

            {isDeleteModalOpen && (
                <DeleteModal
                    onConfirm={confirmDeleteSituation}
                    onCancel={() => setIsDeleteModalOpen(false)}
                    type={'Situation'}
                />
            )}

            <Tooltip
                id="preview_tooltip"
                place="left"
                type="dark"
                effect="solid"
                style={{ zIndex: 9999 }}
            />
        </div>
    );
};

export default EditSituation;
